// ============================================================================
// ListSettingsDialog.tsx — ЕДИНАЯ (SRP/SSoT) форма настроек ЛЮБОГО списка.
// Открывается из панели команд списка («Ещё → Настройки»). Вкладки; пока —
// «Группировки» (управление: вкл/выкл, вариант, поля, вложенность).
// Настройки привязываются к СПИСКУ и ПОЛЬЗОВАТЕЛЮ (user_settings через
// settingsStore) и ПЕРЕКРЫВАЮТ настройки формы/виджета (конфигуратора).
// ============================================================================

import { useState } from 'react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { TabBar } from '@/components/ui/TabBar';
import { FilterListEditor, type FilterRow } from '@/lib/widgets/FilterListEditor';
import { useT } from '@/lib/i18n';

export interface ListGroupField {
  key: string;
  label: string;
  dataType?: string;
}

interface ListSettingsDialogProps {
  open: boolean;
  /** Тип/код списка — для редактора отборов (реквизиты — SSoT сервера). */
  objectType: string;
  objectName: string;
  mode: string;
  by: string;
  /** Пользовательские ОТБОРЫ списка (per-user): применяются вместе с формой (AND). */
  filters: FilterRow[];
  /** Есть пользовательское перекрытие (показать «Сбросить к настройкам формы»). */
  hasOverride: boolean;
  /** Доступные поля списка (колонки) для выбора группировки. */
  fields: ListGroupField[];
  onApply: (mode: string, by: string, filters: FilterRow[]) => void;
  onReset: () => void;
  onClose: () => void;
}

const MODES = [
  { value: '', labelKey: 'list_settings.mode.none' },
  { value: 'sections', labelKey: 'list_settings.mode.sections' },
  { value: 'kanban', labelKey: 'list_settings.mode.kanban' },
  { value: 'chips', labelKey: 'list_settings.mode.chips' },
  { value: 'cards', labelKey: 'list_settings.mode.cards' },
];

function isDateType(dt?: string): boolean {
  return dt === 'date' || dt === 'timestamp' || dt === 'datetime';
}

function specLabel(spec: string, fields: ListGroupField[], t: (key: string) => string): string {
  const [col, part] = spec.split(':');
  const base = fields.find((x) => x.key === col)?.label || col;
  if (part === 'year') return `${t('grouping.year')} · ${base}`;
  if (part === 'month') return `${t('grouping.month')} · ${base}`;
  if (part === 'day') return `${t('grouping.day')} · ${base}`;
  return base;
}

export function ListSettingsDialog({
  open, objectType, objectName, mode, by, filters, hasOverride, fields, onApply, onReset, onClose,
}: ListSettingsDialogProps) {
  const t = useT();
  const [tab, setTab] = useState('group');
  const [localMode, setLocalMode] = useState(mode);
  const [localBy, setLocalBy] = useState<string[]>(by.split(',').map((s) => s.trim()).filter(Boolean));
  const [localFilters, setLocalFilters] = useState<FilterRow[]>(filters || []);
  const [addValue, setAddValue] = useState('');

  const addField = (spec: string) => {
    if (!spec || localBy.includes(spec)) return;
    setLocalBy((prev) => [...prev, spec]);
    setAddValue('');
  };
  const move = (i: number, dir: -1 | 1) => {
    setLocalBy((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };
  const remove = (i: number) => setLocalBy((prev) => prev.filter((_, k) => k !== i));

  return (
    <UniversalWindow
      open={open}
      onClose={onClose}
      title={t('list_settings.title')}
      resizable={false}
      width="w-[min(560px,94vw)]"
    >
      <div className="flex flex-col gap-3 p-1 text-xs">
        <TabBar
          tabs={[{ id: 'group', label: t('list_settings.tab.group') }, { id: 'filters', label: t('list_settings.tab.filters') }]}
          active={tab}
          onChange={setTab}
          group="list-settings"
        />

        {tab === 'group' && (
          <div className="flex flex-col gap-3">
            <div className="space-y-1">
              <div className="text-[10px] text-muted-foreground">{t('list_settings.mode.label')}</div>
              <select
                value={localMode}
                onChange={(e) => setLocalMode(e.target.value)}
                className="w-full rounded-md border bg-input px-2 py-1 text-xs h-7"
              >
                {MODES.map((m) => (
                  <option key={m.value} value={m.value}>{t(m.labelKey)}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <div className="text-[10px] text-muted-foreground">
                {t('list_settings.fields.label')}
              </div>
              {localBy.length === 0 && (
                <div className="text-[11px] text-muted-foreground/70 border border-dashed rounded-md px-2 py-1.5">
                  {t('list_settings.fields.empty')}
                </div>
              )}
              {localBy.map((spec, i) => (
                <div key={spec} className="flex items-center gap-1">
                  <span className="flex-1 truncate rounded-md border border-border/60 bg-muted/30 px-2 py-1">
                    {specLabel(spec, fields, t)}
                  </span>
                  <button type="button" className="rounded border px-1.5 py-0.5 hover:bg-muted disabled:opacity-40"
                    disabled={i === 0} onClick={() => move(i, -1)} title={t('grouping.move_up')}>↑</button>
                  <button type="button" className="rounded border px-1.5 py-0.5 hover:bg-muted disabled:opacity-40"
                    disabled={i === localBy.length - 1} onClick={() => move(i, 1)} title={t('grouping.move_down')}>↓</button>
                  <button type="button" className="rounded border px-1.5 py-0.5 hover:bg-muted text-destructive"
                    onClick={() => remove(i)} title={t('common.remove')}>✕</button>
                </div>
              ))}
              <select
                value={addValue}
                onChange={(e) => addField(e.target.value)}
                className="w-full rounded-md border bg-input px-2 py-1 text-xs h-7"
              >
                <option value="">{t('list_settings.fields.add')}</option>
                {fields.filter((f) => !localBy.includes(f.key)).map((f) => (
                  <option key={f.key} value={f.key}>{f.label}</option>
                ))}
                {fields.filter((f) => isDateType(f.dataType)).flatMap((f) => [
                  <option key={`${f.key}:year`} value={`${f.key}:year`}>{t('grouping.year')} · {f.label}</option>,
                  <option key={`${f.key}:month`} value={`${f.key}:month`}>{t('grouping.month')} · {f.label}</option>,
                  <option key={`${f.key}:day`} value={`${f.key}:day`}>{t('grouping.day')} · {f.label}</option>,
                ])}
              </select>
              <div className="text-[10px] text-muted-foreground leading-snug">
                {t('list_settings.example')}
              </div>
            </div>
          </div>
        )}

        {tab === 'filters' && (
          <div className="flex flex-col gap-2">
            <div className="text-[10px] text-muted-foreground leading-snug">
              {t('list_settings.filters.hint')}
            </div>
            <FilterListEditor
              value={localFilters}
              onChange={setLocalFilters}
              objectType={objectType}
              objectName={objectName}
            />
          </div>
        )}

        <div className="flex items-center gap-2 border-t pt-2">
          {hasOverride && (
            <button
              type="button"
              className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              onClick={() => { onReset(); onClose(); }}
            >
              {t('list_settings.reset')}
            </button>
          )}
          <div className="flex-1" />
          <button type="button" className="rounded-md border px-3 py-1 text-xs hover:bg-muted" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="rounded-md bg-primary text-primary-foreground px-3 py-1 text-xs hover:opacity-90"
            onClick={() => { onApply(localMode, localBy.join(','), localFilters); onClose(); }}
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    </UniversalWindow>
  );
}
